import VerifiedIcon from "@mui/icons-material/Verified";
import { Box } from "@mui/material";
import { useState } from "react";
import { useParams } from "react-router-dom";
import CustomDataGridV2 from "../../../../components/CustomDataGridV2";
import Flex from "../../../../components/Flex";
import Text from "../../../../components/Text";
import ToggleSwitch from "../../../../components/ToggleSwitch";
import {
  DEF_PAGINATION,
  DGT_Pagination,
  applyDefaultColumnSettings,
} from "../../../../utils/utils";
import { GridSortItem } from "@mui/x-data-grid";
import { UserData } from "../../../../dummy-data/user-data";
import { useUpdateProviderUserStatus } from "../../../../queries/use-update-provider-status";
import { formatDateTime } from "../../../../utils/date-time-utility";
import { useProvidersList } from "../../../../queries/provider/setting/use-providers-list";
import { PROVIDER_ROLE_RECORD } from "../../../../constants/master";

interface LocationUserProps {
  locationId: string | undefined;
  searchKey: string;
  xTENANTID: string | null | undefined;
}

const LocationProviderUser = ({
  locationId,
  searchKey,
  xTENANTID,
}: LocationUserProps) => {
  const [pagination, setPagination] = useState<DGT_Pagination>(DEF_PAGINATION);
  const [_gridSorting, setGridSorting] = useState<GridSortItem[]>([]);

  const { data, refetch } = useProvidersList({
    locationUuid: locationId,
    pagination,
    xTENANTID: xTENANTID ?? "",
  });

  const locationProviderList: any = data?.data?.content;

  const COLUMNS = applyDefaultColumnSettings<UserData>([
    {
      // todo change while backend accept sort key
      field: "userInfo",
      flex: 0.6,
      minWidth: 180,
      disableColumnMenu: true,
      headerName: "Name",
      // todo need to fix type
      renderCell: ({ row }: any) => {
        return (
          <Flex gap={"20px"}>
            <Box>
              <Text marginBottom={"3px"} color={"primary.main"}>
                {`${row?.firstName} ${row.lastName}`}
              </Text>
              <Text variant="subtitle2">{row?.age}</Text>
            </Box>
          </Flex>
        );
      },
      sortable: false,
    },
    {
      field: "role",
      flex: 0.3,
      minWidth: 100,
      headerName: "Role",
      sortable: false,
      renderCell: ({ row }) => PROVIDER_ROLE_RECORD[row?.role] ?? "-",
    },
    {
      field: "phone",
      flex: 0.4,
      minWidth: 120,
      headerName: "Contact",
      sortable: false,
    },
    {
      field: "email",
      flex: 0.9,
      minWidth: 240,
      headerName: "Email",
      renderCell: ({ value }) => (
        <Flex alignItems={"center"} gap={"4px"}>
          {value ? (
            <>
              <VerifiedIcon
                color={value.emailVerified ? "success" : "disabled"}
                fontSize="small"
              />
              <Text>{value}</Text>
            </>
          ) : (
            <Text>-</Text>
          )}
        </Flex>
      ),
      sortable: false,
    },
    {
      field: "lastLogin",
      flex: 0.5,
      minWidth: 150,
      headerName: "Last Sign In",
      sortable: false,
      renderCell: ({ ...params }) => {
        return <Text>{params.value ? formatDateTime(params.value) : "-"}</Text>;
      },
    },
    {
      field: "active",
      headerName: "Status",
      renderCell: ({ row, value }) => (
        <ToggleSwitch
          id={row.uuid.toString()}
          checked={value}
          onConfirm={(v, id) => toggleStatus(v, id)}
        />
      ),
      sortable: false,
    },
  ]);

  const params = useParams();

  const { mutateAsync: providerStatusUpdate } = useUpdateProviderUserStatus();

  function toggleStatus<T>(value: boolean, id: T) {
    return providerStatusUpdate({
      providerId: id?.toString() ?? "",
      status: value,
      xTENANTID: params.providerGroupId,
    }).then((res) => {
      refetch();
      return res;
    });
  }

  return (
    <Box sx={{ height: "calc(100vh - 7rem - 200px)" }}>
      <CustomDataGridV2
        searchString={searchKey}
        getRowId={(row) => row.uuid}
        rows={locationProviderList ?? []}
        columns={COLUMNS}
        rowHeight={70}
        paginationModel={pagination}
        onPaginationModelChange={setPagination}
        onSortModelChange={setGridSorting}
        disableRowSelectionOnClick
        getRowClassName={(params) =>
          params.indexRelativeToCurrentPage % 2 === 0 ? "even" : "odd"
        }
      />
    </Box>
  );
};

export default LocationProviderUser;
